import { select } from "@fruugo/utilities";
import { i18n } from "./i18n";
import { updateShippingCost } from "./shipping-cost";

const DEFAULT_QUANTITY = 1;
const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

/** Dispatched on document after the quantity has been validated and applied. */
export const QUANTITY_CHANGE_EVENT = "quantity-selector:change";

export type QuantityChangeDetail = { quantity: number };

declare let window: ProductPage;

let currentSellableQuantity = 0;

export const initQuantitySelector = (sellableQuantity: number) => {
  currentSellableQuantity = sellableQuantity;
  handleQuantityInput(sellableQuantity);
  handleQuantityButtons(sellableQuantity);

  // Validate on inital load, mainly in-case sellableQuantity == 1, we disable the add button
  validate(sellableQuantity);
};

const validate = (sellableQuantity: number) => {
  const input = select<HTMLInputElement>(".js-quantity-input");
  if (!input) return;

  const raw = input.value.trim();
  let value = Number(raw);

  // Reset error message
  updateErrorMessage();

  if (raw === "" || !Number.isFinite(value) || !Number.isInteger(value)) {
    value = DEFAULT_QUANTITY;
  }

  const cap = Math.min(sellableQuantity, MAX_QUANTITY);

  if (value <= MIN_QUANTITY) {
    value = MIN_QUANTITY;
  } else if (value >= cap) {
    value = cap;

    if (cap === sellableQuantity) {
      updateErrorMessage(
        i18n(
          "cart.itemnotavailable.notenoughstock",
          sellableQuantity.toString()
        )
      );
    }
  }

  updateButtonStates(value, sellableQuantity);

  input.value = value.toString();
};

/**
 * Validates and applies a quantity as if the shopper had entered it: same limits and error behaviour as the stepper,
 * refreshes the delivery line and notifies listeners. Returns the quantity actually applied.
 */
export const setQuantity = (quantity: number): number => {
  const input = select<HTMLInputElement>(".js-quantity-input");
  if (!input || !currentSellableQuantity) return quantity;

  input.value = quantity.toString();

  return applyQuantity(currentSellableQuantity);
};

const applyQuantity = (sellableQuantity: number): number => {
  const input = select<HTMLInputElement>(".js-quantity-input");
  if (!input) return DEFAULT_QUANTITY;

  validate(sellableQuantity);

  const quantity = Number(input.value);

  updateShippingCost(
    quantity,
    window.skuInfo,
    window.currentCountry,
    window.currentLang
  );

  document.dispatchEvent(
    new CustomEvent<QuantityChangeDetail>(QUANTITY_CHANGE_EVENT, {
      detail: { quantity },
    })
  );

  return quantity;
};

// Handles the numeric quantity input
const handleQuantityInput = (sellableQuantity: number) => {
  const input = select<HTMLInputElement>(".js-quantity-input");

  if (!input || !sellableQuantity) return;

  input.addEventListener("blur", () => {
    applyQuantity(sellableQuantity);
  });

  // Prevent the form from submitting when hitting return after entering a value
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      input.blur();
    }
  });
};

// Handles the quantity + and - buttons
const handleQuantityButtons = (sellableQuantity: number) => {
  const removeButton = select<HTMLButtonElement>(".js-quantity-remove-button");
  const addButton = select<HTMLButtonElement>(".js-quantity-add-button");
  const input = select<HTMLInputElement>(".js-quantity-input");

  if (!input || !addButton || !removeButton) return;

  const getQuantity = () => {
    const raw = input.value.trim();
    return Number(raw);
  };

  const updateQuantity = (quantity: number) => {
    input.value = quantity.toString();
    applyQuantity(sellableQuantity);
  };

  addButton.addEventListener("click", () => {
    const quantity = getQuantity() + 1;
    updateQuantity(quantity);
  });

  removeButton.addEventListener("click", () => {
    const quantity = getQuantity() - 1;
    updateQuantity(quantity);
  });
};

const updateErrorMessage = (message?: string) => {
  const errorContainer = select<HTMLElement>(".js-quantity-error");
  if (!errorContainer) return;

  if (!message) {
    errorContainer.classList.add("tw:hidden");
    errorContainer.textContent = "";
    return;
  }

  errorContainer.classList.remove("tw:hidden");
  errorContainer.textContent = message;
};

const updateButtonStates = (value: number, sellableQuantity: number) => {
  const removeButton = select<HTMLButtonElement>(".js-quantity-remove-button");
  const addButton = select<HTMLButtonElement>(".js-quantity-add-button");
  if (removeButton) removeButton.disabled = value <= MIN_QUANTITY;
  if (addButton)
    addButton.disabled = value >= Math.min(sellableQuantity, MAX_QUANTITY);
};
